import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Snackbar,
  Alert as MuiAlert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Badge,
  Button,
} from "@mui/material";
import {
  Campaign,
  Schedule,
  Search,
  ExpandMore,
  ExpandLess,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  CloudUpload,
  AccessTime,
  Groups,
  ContactPhone,
  CalendarToday,
  Close,
  Info,
  Download,
  Replay,
  Cancel,
  WarningAmber,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { API_ENDPOINT } from "../../utils/constants";
import { getToken, getHeader } from "../../utils/util";

// ─── Constants ───────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending: {
    label: "Pending",
    color: "warning",
    icon: <HourglassEmpty sx={{ fontSize: 14 }} />,
    bg: "#fffbeb",
    border: "#fde68a",
  },
  uploading: {
    label: "Uploading",
    color: "info",
    icon: <CloudUpload sx={{ fontSize: 14 }} />,
    bg: "#eff6ff",
    border: "#bfdbfe",
  },
  batched: {
    label: "In Progress",
    color: "primary",
    icon: <Schedule sx={{ fontSize: 14 }} />,
    bg: "#eff6ff",
    border: "#bfdbfe",
  },
  completed: {
    label: "Completed",
    color: "success",
    icon: <CheckCircle sx={{ fontSize: 14 }} />,
    bg: "#f0fdf4",
    border: "#bbf7d0",
  },
  failed: {
    label: "Failed",
    color: "error",
    icon: <ErrorIcon sx={{ fontSize: 14 }} />,
    bg: "#fef2f2",
    border: "#fecaca",
  },
  partial_failure: {
    label: "Partial Failure",
    color: "warning",
    icon: <ErrorIcon sx={{ fontSize: 14 }} />,
    bg: "#fffbeb",
    border: "#fde68a",
  },
};

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const TEMPLATE_LABELS = { 0: "Simple IVR", 1: "DTMF Input", 2: "Call Patch" };

// Statuses where cancellation makes sense (campaign still has pending batches)
const CANCELLABLE_STATUSES = ["pending", "batched", "uploading"];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: "default" };
  return (
    <Chip
      size="small"
      icon={cfg.icon}
      label={cfg.label}
      color={cfg.color}
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.72rem" }}
    />
  );
};

const ProgressBar = ({ launched, total, failed }) => {
  const launchedPct = total > 0 ? (launched / total) * 100 : 0;
  const failedPct = total > 0 ? (failed / total) * 100 : 0;
  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" mb={0.5}>
        <Typography variant="caption" color="text.secondary">
          {launched}/{total} batches launched
          {failed > 0 && (
            <Typography
              component="span"
              variant="caption"
              color="error.main"
              ml={1}
            >
              ({failed} failed)
            </Typography>
          )}
        </Typography>
        <Typography variant="caption" fontWeight={600} color="primary">
          {Math.round(launchedPct)}%
        </Typography>
      </Stack>
      <Box
        sx={{
          position: "relative",
          height: 6,
          borderRadius: 3,
          bgcolor: "#e5e7eb",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            height: "100%",
            width: `${launchedPct}%`,
            background: "linear-gradient(90deg, #3b82f6, #8b5cf6)",
            borderRadius: 3,
            transition: "width 0.4s ease",
          }}
        />
        {failedPct > 0 && (
          <Box
            sx={{
              position: "absolute",
              left: `${launchedPct}%`,
              top: 0,
              height: "100%",
              width: `${failedPct}%`,
              bgcolor: "#ef4444",
              borderRadius: 3,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

// ─── Cancel Confirmation Dialog ───────────────────────────────────────────────
const CancelConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  campaignName,
  pendingBatches,
  cancelling,
}) => {
  return (
    <Dialog
      open={open}
      onClose={!cancelling ? onClose : undefined}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: "1px solid #fecaca",
          boxShadow: "0 20px 60px rgba(239, 68, 68, 0.15)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#fef2f2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WarningAmber sx={{ fontSize: 22, color: "#ef4444" }} />
          </Box>
          <Box>
            <Typography fontWeight={700} fontSize="1rem">
              Cancel Pending Batches?
            </Typography>
            <Typography variant="caption" color="text.secondary">
              This action cannot be undone
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Divider sx={{ mb: 2 }} />

        {/* Campaign name highlight */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            mb: 2,
            borderRadius: 2,
            bgcolor: "#fafafa",
            borderColor: "#e5e7eb",
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Campaign sx={{ fontSize: 16, color: "#3b82f6" }} />
            <Typography variant="body2" fontWeight={600} noWrap>
              {campaignName}
            </Typography>
          </Stack>
        </Paper>

        <Typography variant="body2" color="text.secondary" lineHeight={1.7}>
          All{" "}
          <Typography
            component="span"
            fontWeight={700}
            color="error.main"
            variant="body2"
          >
            {pendingBatches} pending batch
            {pendingBatches !== 1 ? "es" : ""}
          </Typography>{" "}
          will be permanently cancelled. Batches that have already been launched
          will continue running and are not affected.
        </Typography>

        <Alert
          severity="warning"
          icon={<HourglassEmpty fontSize="inherit" />}
          sx={{ mt: 2, py: 0.75, fontSize: "0.75rem", borderRadius: 2 }}
        >
          Only <strong>pending</strong> batches are cancelled — launched batches
          remain active.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={cancelling}
          size="small"
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
        >
          Keep Batches
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={onConfirm}
          disabled={cancelling}
          size="small"
          startIcon={
            cancelling ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <Cancel sx={{ fontSize: 16 }} />
            )
          }
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none", bgcolor: "#dc2626" },
          }}
        >
          {cancelling ? "Cancelling…" : "Yes, Cancel Batches"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Batch Detail Dialog ──────────────────────────────────────────────────────
const BatchDetailDialog = ({ parentId, campaignName, open, onClose }) => {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  const [downloadingId, setDownloadingId] = useState(null);
  const [downloadingContactsId, setDownloadingContactsId] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchBatches = useCallback(async () => {
    if (!parentId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.set("status", statusFilter);

      const res = await fetch(
        `${API_ENDPOINT}/voice/scheduled-campaigns/${parentId}/batches?${params}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const json = await res.json();
      if (json.success) {
        setBatches(json.data);
        setTotalPages(json.pagination.totalPages);
      }
    } catch (e) {
      console.error("Failed to fetch batches:", e);
    } finally {
      setLoading(false);
    }
  }, [parentId, page, statusFilter]);

  useEffect(() => {
    if (open) fetchBatches();
  }, [open, fetchBatches]);

  useEffect(() => {
    if (!open) {
      setSnackbar((s) => ({ ...s, open: false }));
      setDownloadingId(null);
      setDownloadingContactsId(null);
      setRetryingId(null);
    }
  }, [open]);

  const handleDownloadReport = async (campaignId) => {
    setDownloadingId(campaignId);
    try {
      const res = await fetch(`${API_ENDPOINT}/voice/download-request`, {
        method: "POST",
        ...getHeader(),
        body: JSON.stringify({ campaignId }),
      });
      const result = await res.json();
      setSnackbar({
        open: true,
        message:
          res.ok && result.success !== false
            ? result.message || "Report request submitted successfully"
            : result.message || "Failed to request report",
        severity: res.ok && result.success !== false ? "success" : "error",
      });
    } catch (e) {
      setSnackbar({
        open: true,
        message: "Failed to request report",
        severity: "error",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadContacts = async (batchId, batchIndex) => {
    setDownloadingContactsId(batchId);
    try {
      const res = await fetch(
        `${API_ENDPOINT}/voice/get-full-batch/${batchId}`,
        { method: "GET", headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const result = await res.json();

      if (res.ok && result.contacts && Array.isArray(result.contacts)) {
        if (result.contacts.length === 0) {
          setSnackbar({
            open: true,
            message: "No contacts found for this batch.",
            severity: "warning",
          });
          return;
        }
        const excelData = result.contacts.map((contact) => ({
          "Contact Number": contact,
        }));
        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Contacts");
        XLSX.writeFile(workbook, `Batch_${batchIndex + 1}_Contacts.xlsx`);
        setSnackbar({
          open: true,
          message: "Contacts downloaded successfully",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "Failed to load contacts data",
          severity: "error",
        });
      }
    } catch (e) {
      setSnackbar({
        open: true,
        message: "Failed to download contacts",
        severity: "error",
      });
    } finally {
      setDownloadingContactsId(null);
    }
  };

  const handleRetryBatch = async (batchMongoId, batchIndex) => {
    setRetryingId(batchMongoId);
    try {
      const res = await fetch(
        `${API_ENDPOINT}/voice/retry-bacth-scheduled/${batchMongoId}`,
        { method: "POST", headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const result = await res.json();
      setSnackbar({
        open: true,
        message: res.ok
          ? result.message ||
            `Batch ${batchIndex + 1} retry submitted successfully`
          : result.message || `Failed to retry Batch ${batchIndex + 1}`,
        severity: res.ok ? "success" : "error",
      });
      if (res.ok) fetchBatches();
    } catch (e) {
      setSnackbar({
        open: true,
        message: `Failed to retry Batch ${batchIndex + 1}`,
        severity: "error",
      });
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>Batch List</DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        #
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        STATUS
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        CONTACTS
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        SCHEDULED AT
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        LAUNCHED AT
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        CAMPAIGN ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        ERROR
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        REPORT
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        GET DATA
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batches.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          sx={{
                            textAlign: "center",
                            py: 4,
                            color: "text.secondary",
                          }}
                        >
                          No batches found
                        </TableCell>
                      </TableRow>
                    ) : (
                      batches.map((b) => {
                        const batchMongoId = b._id || b.id;
                        const isRetryCandidate =
                          b.campaignId === null ||
                          b.campaignId === "-1" ||
                          b.error === "socket hang up";

                        return (
                          <TableRow
                            key={batchMongoId}
                            hover
                            sx={{
                              bgcolor:
                                b.status === "failed" ? "#fff5f5" : "inherit",
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {b.batchIndex + 1}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <StatusChip status={b.status} />
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">
                                {(b.contactCount || 0).toLocaleString()}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.72rem",
                                }}
                              >
                                {formatDate(b.scheduledAt)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontFamily: "monospace",
                                  fontSize: "0.72rem",
                                }}
                              >
                                {formatDate(b.launchedAt)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {b.campaignId ? (
                                <Chip
                                  size="small"
                                  label={b.campaignId}
                                  variant="outlined"
                                  color={isRetryCandidate ? "error" : "default"}
                                  sx={{
                                    fontFamily: "monospace",
                                    fontSize: "0.68rem",
                                  }}
                                />
                              ) : (
                                "—"
                              )}
                            </TableCell>

                            <TableCell sx={{ maxWidth: 180 }}>
                              {b.error ? (
                                <Tooltip title={b.error} arrow>
                                  <Typography
                                    variant="caption"
                                    color="error.main"
                                    sx={{
                                      cursor: "help",
                                      textDecoration: "underline dotted",
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                    }}
                                  >
                                    {b.error}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                "—"
                              )}
                            </TableCell>

                            <TableCell>
                              {isRetryCandidate ? (
                                <Tooltip
                                  title={`Retry Batch ${
                                    b.batchIndex + 1
                                  } — launched but campaign was not sent`}
                                  arrow
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="warning"
                                      disabled={retryingId === batchMongoId}
                                      onClick={() =>
                                        handleRetryBatch(
                                          batchMongoId,
                                          b.batchIndex
                                        )
                                      }
                                      sx={{
                                        border: "1px solid",
                                        borderColor: "warning.main",
                                        borderRadius: 1.5,
                                        p: 0.6,
                                        "&:hover": { bgcolor: "warning.50" },
                                      }}
                                    >
                                      {retryingId === batchMongoId ? (
                                        <CircularProgress
                                          size={14}
                                          color="warning"
                                        />
                                      ) : (
                                        <Replay sx={{ fontSize: 15 }} />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : b.campaignId ? (
                                <Tooltip
                                  title={`Download report for campaign ${b.campaignId}`}
                                  arrow
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={downloadingId === b.campaignId}
                                      onClick={() =>
                                        handleDownloadReport(b.campaignId)
                                      }
                                      sx={{
                                        border: "1px solid",
                                        borderColor: "primary.light",
                                        borderRadius: 1.5,
                                        p: 0.6,
                                        "&:hover": { bgcolor: "primary.50" },
                                      }}
                                    >
                                      {downloadingId === b.campaignId ? (
                                        <CircularProgress
                                          size={14}
                                          color="primary"
                                        />
                                      ) : (
                                        <Download sx={{ fontSize: 15 }} />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              ) : (
                                <Typography
                                  variant="caption"
                                  color="text.disabled"
                                >
                                  —
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              <Tooltip
                                title={`Download contacts for Batch ${
                                  b.batchIndex + 1
                                }`}
                                arrow
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="success"
                                    disabled={
                                      downloadingContactsId === batchMongoId
                                    }
                                    onClick={() =>
                                      handleDownloadContacts(
                                        batchMongoId,
                                        b.batchIndex
                                      )
                                    }
                                    sx={{
                                      border: "1px solid",
                                      borderColor: "success.light",
                                      borderRadius: 1.5,
                                      p: 0.6,
                                      "&:hover": { bgcolor: "success.50" },
                                    }}
                                  >
                                    {downloadingContactsId === batchMongoId ? (
                                      <CircularProgress
                                        size={14}
                                        color="success"
                                      />
                                    ) : (
                                      <Groups sx={{ fontSize: 15 }} />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    size="small"
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", fontWeight: 500 }}
          icon={
            snackbar.severity === "success" ? (
              <CheckCircle fontSize="inherit" />
            ) : (
              <ErrorIcon fontSize="inherit" />
            )
          }
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

// ─── Campaign Row ──────────────────────────────────────────────────────────────
const CampaignRow = ({ campaign, onCancelSuccess }) => {
  const [detailOpen, setDetailOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const cfg = STATUS_CONFIG[campaign.status] || {};

  const isCancellable = CANCELLABLE_STATUSES.includes(campaign.status);

  // Estimate pending batches = totalBatches - launchedBatches - failedBatches
  const pendingBatchCount = Math.max(
    0,
    (campaign.totalBatches || 0) -
      (campaign.launchedBatches || 0) -
      (campaign.failedBatches || 0)
  );

  const handleCancelConfirm = async () => {
    setCancelling(true);
    try {
      const res = await fetch(
        `${API_ENDPOINT}/voice/scheduled-campaigns/${campaign.parentId}/cancel`,
        {
          method: "PATCH",
          headers: { Authorization: `Bearer ${getToken()}` },
        }
      );
      const result = await res.json();

      if (res.ok && result.success) {
        setCancelDialogOpen(false);
        setSnackbar({
          open: true,
          message:
            result.modifiedCount > 0
              ? `Successfully cancelled ${result.modifiedCount} pending batch${
                  result.modifiedCount !== 1 ? "es" : ""
                }`
              : result.message || "Cancellation processed",
          severity: "success",
        });
        // Notify parent to refresh campaign list
        if (onCancelSuccess) onCancelSuccess();
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to cancel batches",
          severity: "error",
        });
      }
    } catch (e) {
      console.error("Cancel batches error:", e);
      setSnackbar({
        open: true,
        message: "Failed to cancel pending batches",
        severity: "error",
      });
    } finally {
      setCancelling(false);
    }
  };

  const handleViewReport = () => {
    // Add your report routing or download logic here
    console.log("Viewing report for campaign:", campaign.id);
    //open in new tab react  /home/scheduled-voice-campaigns/${campaign.id}
    window.open(
      `/home/${campaign.id}/scheduled-voice-campaigns`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          borderColor: cfg.border || "#e5e7eb",
          bgcolor: cfg.bg || "#fff",
          transition: "box-shadow 0.2s",
          "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.08)" },
        }}
      >
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2} alignItems="flex-start">
            {/* Left: name + status */}
            <Grid item xs={12} sm={4}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Campaign sx={{ fontSize: 18, color: "#3b82f6" }} />
                  <Typography fontWeight={700} variant="body1" noWrap>
                    {campaign.campaignName}
                  </Typography>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <StatusChip status={campaign.status} />
                  <Chip
                    size="small"
                    label={
                      TEMPLATE_LABELS[campaign.templateId] ||
                      campaign.templateId
                    }
                    variant="outlined"
                    sx={{ fontSize: "0.68rem" }}
                  />
                  <Chip
                    size="small"
                    label={campaign.location || "—"}
                    variant="outlined"
                    sx={{ fontSize: "0.68rem" }}
                  />
                </Stack>
              </Stack>
            </Grid>

            {/* Center: stats */}
            <Grid item xs={12} sm={5}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <ContactPhone sx={{ fontSize: 14, color: "#6b7280" }} />
                    <Typography variant="caption" color="text.secondary">
                      Total Contacts
                    </Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={600}>
                    {(campaign.totalContacts || 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Groups sx={{ fontSize: 14, color: "#6b7280" }} />
                    <Typography variant="caption" color="text.secondary">
                      Batch Size
                    </Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={600}>
                    {(campaign.batchSize || 0).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <ProgressBar
                    launched={campaign.launchedBatches}
                    total={campaign.totalBatches}
                    failed={campaign.failedBatches}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Right: schedule info + actions */}
            <Grid item xs={12} sm={3}>
              <Stack spacing={0.5} alignItems={{ sm: "flex-end" }}>
                {campaign.nextScheduledAt && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <AccessTime sx={{ fontSize: 13, color: "#f59e0b" }} />
                    <Typography variant="caption" color="text.secondary">
                      Next batch:
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="warning.main"
                    >
                      {formatDate(campaign.nextScheduledAt)}
                    </Typography>
                  </Stack>
                )}
                {campaign.lastLaunchedAt && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CheckCircle sx={{ fontSize: 13, color: "#10b981" }} />
                    <Typography variant="caption" color="text.secondary">
                      Last launch:
                    </Typography>
                    <Typography
                      variant="caption"
                      fontWeight={600}
                      color="success.main"
                    >
                      {formatDate(campaign.lastLaunchedAt)}
                    </Typography>
                  </Stack>
                )}
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <CalendarToday sx={{ fontSize: 13, color: "#6b7280" }} />
                  <Typography variant="caption" color="text.secondary">
                    Created {formatDate(campaign.createdAt)}
                  </Typography>
                </Stack>

                {/* ── View Batches ── */}
                <Tooltip title="View batch breakdown">
                  <Chip
                    size="small"
                    label={`View ${campaign.totalBatches} batches`}
                    color="primary"
                    variant="outlined"
                    onClick={() => setDetailOpen(true)}
                    sx={{ cursor: "pointer", mt: 0.5, fontWeight: 600 }}
                    icon={<Info sx={{ fontSize: "14px !important" }} />}
                  />
                </Tooltip>

                {/* ── View Report (Completed status only) ── */}
                {campaign.status === "completed" && (
                  <Tooltip title="View campaign report">
                    <Chip
                      size="small"
                      label="View Report"
                      color="success"
                      variant="outlined"
                      onClick={handleViewReport}
                      sx={{ cursor: "pointer", mt: 0.5, fontWeight: 600 }}
                      icon={<Download sx={{ fontSize: "14px !important" }} />}
                    />
                  </Tooltip>
                )}

                {/* ── Cancel Pending Batches ── */}
                {isCancellable && pendingBatchCount > 0 && (
                  <Tooltip
                    title={`Cancel ${pendingBatchCount} pending batch${
                      pendingBatchCount !== 1 ? "es" : ""
                    }`}
                  >
                    <Chip
                      size="small"
                      label={`Cancel ${pendingBatchCount} pending`}
                      color="error"
                      variant="outlined"
                      onClick={() => setCancelDialogOpen(true)}
                      sx={{
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: "0.68rem",
                        borderStyle: "dashed",
                        mt: 0.5,
                        "&:hover": {
                          bgcolor: "#fef2f2",
                          borderStyle: "solid",
                        },
                        transition: "all 0.15s ease",
                      }}
                      icon={<Cancel sx={{ fontSize: "14px !important" }} />}
                    />
                  </Tooltip>
                )}
              </Stack>
            </Grid>
          </Grid>

          {/* Active days + blackout */}
          {(campaign.activeDays?.length > 0 ||
            campaign.nonActiveHours?.length > 0) && (
            <>
              <Divider sx={{ my: 1.5 }} />
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {campaign.activeDays?.length > 0 && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <CalendarToday sx={{ fontSize: 13, color: "#6b7280" }} />
                    <Typography variant="caption" color="text.secondary">
                      Active days:
                    </Typography>
                    {campaign.activeDays.sort().map((d) => (
                      <Chip
                        key={d}
                        size="small"
                        label={DAY_LABELS[d]}
                        sx={{ height: 18, fontSize: "0.65rem", px: 0.2 }}
                      />
                    ))}
                  </Stack>
                )}
                {campaign.nonActiveHours?.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    <AccessTime sx={{ fontSize: 13, color: "#6b7280" }} />
                    <Typography variant="caption" color="text.secondary">
                      Blackout:
                    </Typography>
                    {campaign.nonActiveHours.map((w, i) => (
                      <Chip
                        key={i}
                        size="small"
                        label={`${w.start}–${w.end}`}
                        variant="outlined"
                        color="warning"
                        sx={{ height: 18, fontSize: "0.65rem", px: 0.2 }}
                      />
                    ))}
                  </Stack>
                )}
                {campaign.intervalMinutes > 0 && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Schedule sx={{ fontSize: 13, color: "#6b7280" }} />
                    <Typography variant="caption" color="text.secondary">
                      Interval: <strong>{campaign.intervalMinutes} min</strong>
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </>
          )}

          {/* Error banner */}
          {campaign.lastError && (
            <Alert
              severity="error"
              sx={{ mt: 1.5, py: 0.5, fontSize: "0.75rem" }}
            >
              {campaign.lastError}
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Batch detail dialog */}
      <BatchDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        parentId={campaign.parentId}
        campaignName={campaign.campaignName}
      />

      {/* Cancel confirmation dialog */}
      <CancelConfirmDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelConfirm}
        campaignName={campaign.campaignName}
        pendingBatches={pendingBatchCount}
        cancelling={cancelling}
      />

      {/* Snackbar for cancel feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", fontWeight: 500 }}
          icon={
            snackbar.severity === "success" ? (
              <CheckCircle fontSize="inherit" />
            ) : (
              <ErrorIcon fontSize="inherit" />
            )
          }
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────
const ScheduledCampaignsList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page, limit });
      if (statusFilter) params.set("status", statusFilter);
      if (search.trim()) params.set("search", search.trim());

      const res = await fetch(
        `${API_ENDPOINT}/voice/scheduled-campaigns?${params}`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      const json = await res.json();

      if (!json.success) throw new Error(json.message || "Failed to fetch");

      setCampaigns(json.data);
      setTotalPages(json.pagination.totalPages);
      setTotalCount(json.pagination.total);
      setLastRefresh(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, search]);

  useEffect(() => {
    fetchCampaigns();
    const timer = setInterval(fetchCampaigns, 30_000);
    return () => clearInterval(timer);
  }, [fetchCampaigns]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search]);

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") setSearch(searchInput);
  };

  const STATUS_FILTER_OPTIONS = [
    { value: "", label: "All Statuses" },
    { value: "pending", label: "Pending" },
    { value: "uploading", label: "Uploading" },
    { value: "batched", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
    { value: "partial_failure", label: "Partial Failure" },
  ];

  return (
    <Box sx={{ py: 4, px: 2 }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Schedule sx={{ fontSize: 32, color: "#3b82f6" }} />
            <Typography variant="h5" fontWeight={700}>
              Scheduled Campaigns
            </Typography>
            {totalCount > 0 && (
              <Chip
                label={`${totalCount} total`}
                color="primary"
                size="small"
              />
            )}
          </Stack>
          {lastRefresh && (
            <Typography variant="caption" color="text.secondary" ml={6}>
              Last updated: {lastRefresh.toLocaleTimeString()} · Auto-refreshes
              every 30s
            </Typography>
          )}
        </Box>
        <Tooltip title="Refresh now">
          <IconButton onClick={fetchCampaigns} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <Refresh />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={3}>
        <TextField
          size="small"
          placeholder="Search campaign name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={handleSearchKeyDown}
          onBlur={() => setSearch(searchInput)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: "#9ca3af" }} />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1, maxWidth: 340 }}
        />
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {/* ── Error ──────────────────────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Loading skeleton ────────────────────────────────────────────────── */}
      {loading && campaigns.length === 0 && (
        <Stack spacing={2}>
          {[...Array(4)].map((_, i) => (
            <Paper
              key={i}
              variant="outlined"
              sx={{ borderRadius: 2, p: 2.5, height: 100 }}
            >
              <LinearProgress sx={{ borderRadius: 1 }} />
            </Paper>
          ))}
        </Stack>
      )}

      {/* ── Empty ──────────────────────────────────────────────────────────── */}
      {!loading && campaigns.length === 0 && !error && (
        <Paper
          variant="outlined"
          sx={{ borderRadius: 2, textAlign: "center", py: 8 }}
        >
          <Schedule sx={{ fontSize: 56, color: "#d1d5db", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No scheduled campaigns found
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            {statusFilter || search
              ? "Try adjusting your filters"
              : "Campaigns with batching enabled will appear here"}
          </Typography>
        </Paper>
      )}

      {/* ── Campaign list ───────────────────────────────────────────────────── */}
      <Stack
        spacing={2}
        sx={{ opacity: loading ? 0.6 : 1, transition: "opacity 0.2s" }}
      >
        {campaigns.map((campaign) => (
          <CampaignRow
            key={campaign.id}
            campaign={campaign}
            onCancelSuccess={fetchCampaigns}
          />
        ))}
      </Stack>

      {/* ── Pagination ─────────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            mt: 4,
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Page {page} of {totalPages} · {totalCount} campaigns
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, v) => setPage(v)}
            color="primary"
            showFirstButton
            showLastButton
            siblingCount={1}
          />
        </Box>
      )}
    </Box>
  );
};

export default ScheduledCampaignsList;
